import { QueryClient } from "@tanstack/react-query";

import { isUnreachable } from "@/services/api";

/**
 * The one cache, in a module of its own.
 *
 * It lives here rather than in `main.tsx` so signing out can empty it: the
 * cache holds one person's profile, permissions and policy, and the next
 * person to sign in on this tab must not be handed them.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // The axios interceptor already refreshes once on a 401; retrying a
      // genuine 401 or 403 would only produce noise.
      retry: (failureCount, error) => {
        // Nothing answered at all. A retry asks the same dead socket the same
        // question, and it is not one retry — it is every query the screen
        // holds, three times each, which is the burst in the network panel
        // whenever the API is down. The one thing worth retrying against a
        // server that is not there is a person deciding to, which is what the
        // server-unavailable screen's button does.
        //
        // Checked before the status test below, because an error with no
        // response has no status to fail that test.
        if (isUnreachable(error)) return false;
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * A read nobody is waiting for any more is cancelled.
 *
 * Switching menu or tab unmounts the screen, but react-query keeps its
 * in-flight fetch running to warm the cache — so an abandoned query held a
 * connection open and PostgreSQL went on sorting rows for an answer nobody
 * would read. Measured with a slow response: leaving a screen mid-request
 * still finished 200 long after.
 *
 * Cancelling here aborts the request's signal, which every `queryFn` now
 * takes, which closes the socket. The backend's own watcher
 * (`app/core/cancellation.py`) sees the disconnect and asks PostgreSQL to
 * cancel the statement — so one click stops the work at both ends.
 *
 * `observerRemoved` fires only for a query something was actually rendering,
 * which is what keeps this off the router's business: a preload or a route
 * loader fetches through `ensureQueryData` and never has an observer, so it
 * cannot reach this and cannot be cancelled out from under a navigation.
 * Mutations are not in this cache at all — a save already sent must land.
 */
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "observerRemoved") return;
  const { query } = event;
  // Asked again a tick later, not now: StrictMode mounts every component
  // twice in development, removing the observer and adding it straight back,
  // and answering immediately would cancel the first fetch of every screen.
  setTimeout(() => {
    if (query.getObserversCount() > 0) return;
    if (query.state.fetchStatus !== "fetching") return;
    // Reverted rather than left half-done, so coming back refetches from a
    // clean state instead of showing whatever had arrived.
    void query.cancel({ revert: true });
  }, 0);
});
